// Bestiary milestones: diamonds for catching a share of a region's species,
// claimed once each from the Bestiary page. A species counts once the player
// has caught one (ascending an own catch counts as catching the next stage);
// creatures bought on the market, and their ascensions, never count. Bosses are not part
// of a region's total. Diamonds stay on the account and never buy odds.
import { LINES, type RegionId } from './species.ts';

export interface DexMilestone { pct: number; diamonds: number }

/**
 * One-time rewards per region, 10 diamonds each (was 25 until Cole's 2026-10-20 cut to 15-20 free diamonds a
 * month), in five steps so a new region's first payout comes early. For scale: the daily claim pays 3 a week, the
 * weekly chest none, and trainer level-ups pay no diamonds.
 */
export const DEX_MILESTONES: DexMilestone[] = [
  { pct: 20, diamonds: 2 },
  { pct: 40, diamonds: 2 },
  { pct: 60, diamonds: 2 },
  { pct: 80, diamonds: 2 },
  { pct: 100, diamonds: 2 },
];

/** Every species the Bestiary lists for a region (all stages of its lines). */
export function regionSpecies(region: RegionId): string[] {
  return LINES.filter((l) => l.region === region).flatMap((l) => l.stages.map((s) => s.id));
}

/** Species caught needed for a milestone in a region of `total` species. */
export const milestoneNeed = (m: DexMilestone, total: number) => Math.ceil((total * m.pct) / 100);

export interface DexProgress {
  caught: number;
  total: number;
  /** Milestones reached so far (0-5). */
  reached: number;
  /** Milestones already claimed. */
  claimed: number;
  /** Diamonds waiting to be claimed. */
  claimable: number;
}

export function dexProgress(dex: Record<string, { caught: number }>, claimed: Record<string, number> | undefined, region: RegionId): DexProgress {
  const ids = regionSpecies(region);
  const caught = ids.filter((id) => (dex[id]?.caught ?? 0) > 0).length;
  const reached = DEX_MILESTONES.filter((m) => caught >= milestoneNeed(m, ids.length)).length;
  const done = Math.min(claimed?.[region] ?? 0, reached);
  const claimable = DEX_MILESTONES.slice(done, reached).reduce((a, m) => a + m.diamonds, 0);
  return { caught, total: ids.length, reached, claimed: done, claimable };
}
